import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import NotificationPreferences from '../components/components/NotificationPreferences';

const UserSettings = () => {
  const { team } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Change name state
  const [name, setName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMessage, setNameMessage] = useState('');
  const [nameError, setNameError] = useState('');

  // Change password state (step 1: request OTP)
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordRequestLoading, setPasswordRequestLoading] = useState(false);
  const [passwordRequestError, setPasswordRequestError] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  // Change password state (step 2: verify OTP)
  const [otp, setOtp] = useState('');
  const [passwordVerifyLoading, setPasswordVerifyLoading] = useState(false);
  const [passwordVerifyError, setPasswordVerifyError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/auth/me');
        const userData = response.data.user;
        setUser(userData);
        setName(userData.name || '');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  const handleSaveName = async (e) => {
    e.preventDefault();
    setNameSaving(true);
    setNameMessage('');
    setNameError('');

    try {
      await axiosInstance.put('/auth/update-profile', { name: name.trim() });
      setUser((prev) => (prev ? { ...prev, name: name.trim() } : null));
      setNameMessage('Name updated successfully');
      // Update localStorage User if it exists
      try {
        const storedUser = localStorage.getItem('User');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          parsed.name = name.trim();
          localStorage.setItem('User', JSON.stringify(parsed));
        }
      } catch (_) {}
    } catch (err) {
      setNameError(err.response?.data?.message || 'Failed to update name');
    } finally {
      setNameSaving(false);
    }
  };

  const handlePasswordRequest = async (e) => {
    e.preventDefault();
    setPasswordRequestLoading(true);
    setPasswordRequestError('');

    if (newPassword !== confirmPassword) {
      setPasswordRequestError('New password and confirmation do not match');
      setPasswordRequestLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setPasswordRequestError('Password must be at least 8 characters long');
      setPasswordRequestLoading(false);
      return;
    }

    try {
      await axiosInstance.post('/auth/change-password-request', {
        currentPassword,
        newPassword,
        confirmPassword
      });
      setOtpSent(true);
    } catch (err) {
      setPasswordRequestError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setPasswordRequestLoading(false);
    }
  };

  const handlePasswordVerify = async (e) => {
    e.preventDefault();
    setPasswordVerifyLoading(true);
    setPasswordVerifyError('');
    setPasswordSuccess('');

    try {
      await axiosInstance.post('/auth/change-password-verify', {
        otp: otp.trim(),
        newPassword
      });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setOtp('');
      setOtpSent(false);
    } catch (err) {
      setPasswordVerifyError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordVerifyLoading(false);
    }
  };

  const handleResetPasswordFlow = () => {
    setOtpSent(false);
    setOtp('');
    setPasswordRequestError('');
    setPasswordVerifyError('');
    setPasswordSuccess('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded-lg" style={{ fontFamily: 'Raleway, sans-serif' }}>
          {error || 'User not found'}
        </div>
      </div>
    );
  }

  const isEmailUser = user.isEmailUser === true || user.auth_method === 'email';

  return (
    <div className="max-w-3xl p-6" style={{ fontFamily: 'Raleway, sans-serif' }}>
      <h1 className="text-2xl font-semibold text-[#0f172a] mb-2">User Settings</h1>
      <p className="text-sm text-[#475569] mb-8">Manage your account details</p>

      {/* Change Name - all users */}
      <div className="mb-10">
        <h2 className="text-lg font-medium text-[#0f172a] mb-3">Change Name</h2>
        <form onSubmit={handleSaveName} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#475569] mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 border border-[#DDE2EE] rounded-lg text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
              placeholder="Your name"
              required
            />
          </div>
          {nameMessage && (
            <div className="text-sm text-green-600">{nameMessage}</div>
          )}
          {nameError && (
            <div className="text-sm text-red-600">{nameError}</div>
          )}
          <button
            type="submit"
            disabled={nameSaving}
            className="px-4 py-2 rounded-md font-medium text-white bg-[#4b57aa] hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
          >
            {nameSaving ? 'Saving...' : 'Save Name'}
          </button>
        </form>
      </div>

      {/* Change Password - email/password users only */}
      {isEmailUser ? (
        <div>
          <h2 className="text-lg font-medium text-[#0f172a] mb-3">Change Password</h2>

          {!otpSent ? (
            <form onSubmit={handlePasswordRequest} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-[#DDE2EE] rounded-lg text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
                  placeholder="Enter your current password"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-[#DDE2EE] rounded-lg text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-[#DDE2EE] rounded-lg text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                />
              </div>
              {passwordRequestError && (
                <div className="text-sm text-red-600">{passwordRequestError}</div>
              )}
              <button
                type="submit"
                disabled={passwordRequestLoading}
                className="px-4 py-2 rounded-md font-medium text-white bg-[#4b57aa] hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
              >
                {passwordRequestLoading ? 'Sending...' : 'Reset Password'}
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasswordVerify} className="space-y-3">
              <p className="text-sm text-[#475569]">
                A verification code has been sent to <strong>{user.email}</strong>. Enter it below to complete the password change.
              </p>
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-1">Verification Code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-2.5 border border-[#DDE2EE] rounded-lg text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#8979FF]/20 focus:border-[#8979FF]"
                  placeholder="Enter 6-digit code from email"
                  maxLength={6}
                  required
                />
              </div>
              {passwordVerifyError && (
                <div className="text-sm text-red-600">{passwordVerifyError}</div>
              )}
              {passwordSuccess && (
                <div className="text-sm text-green-600">{passwordSuccess}</div>
              )}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={passwordVerifyLoading}
                  className="px-4 py-2 rounded-md font-medium text-white bg-[#4b57aa] hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
                >
                  {passwordVerifyLoading ? 'Verifying...' : 'Verify & Change Password'}
                </button>
                <button
                  type="button"
                  onClick={handleResetPasswordFlow}
                  className="px-4 py-2 rounded-lg font-medium border border-[#DDE2EE] text-[#475569] hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      ) : (
        <div className="text-sm text-[#475569]">
          <p>You signed in with Google. Password change is not available for Google accounts.</p>
          <p className="mt-1">To change your name, use the form above.</p>
        </div>
      )}

      <div className="mt-10 pt-8 border-t border-gray-200">
        <NotificationPreferences />
      </div>
    </div>
  );
};

export default UserSettings;
